import { DOCUMENT, ViewportScroller } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule, isDevMode } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { SuiModalModule, SuiModalService } from '@giomamaladze/ng2-semantic-ui';
import { EffectsModule } from '@ngrx/effects';
import {
  RouterState,
  StoreRouterConnectingModule,
  routerReducer,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { DeviceDetectorService } from 'ngx-device-detector';
import { IConfig, NgxMaskModule } from 'ngx-mask';

import { PatientWebFeatureShellModule } from '@patient-ui/patient-web/feature-shell';
import {
  AuthConfigService,
  EnvironmentService,
} from '@patient-ui/shared-ui/utils';

import { AppComponent } from './app.component';
import { OktaAuthConfig } from './auth-config';
import { ConfirmModal, ConfirmModalComponent } from './modal/confirm.component';
import { AddHeaderInterceptor } from './shared/http/addHeaderInterceptor';
import { createResultDetailViewportScroller } from './shared/result-detail-viewport-scroller.factory';

export const options: Partial<IConfig> | (() => Partial<IConfig>) = {};

@NgModule({
  declarations: [AppComponent, ConfirmModalComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    NgxMaskModule.forRoot(options),
    HttpClientModule,
    ReactiveFormsModule,
    RouterModule.forRoot([], {
      initialNavigation: 'enabledNonBlocking',
      scrollPositionRestoration: 'enabled',
      useHash: false,
      enableTracing: isDevMode(),
    }),
    PatientWebFeatureShellModule,
    StoreModule.forRoot(
      { router: routerReducer },
      {
        metaReducers: [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
          strictActionSerializability: false,
          strictStateSerializability: false,
        },
      }
    ),
    OktaAuthModule,
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      name: 'Patient Web NgRx Store',
      logOnly: !isDevMode(),
    }),
    StoreRouterConnectingModule.forRoot({ routerState: RouterState.Minimal }),
    SuiModalModule,
  ],

  providers: [
    {
      provide: ViewportScroller,
      useFactory: createResultDetailViewportScroller,
      deps: [DOCUMENT],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (es: EnvironmentService, acs: AuthConfigService) => () =>
        es.loadEnvironment().then(() => acs.setupAuthConfig()),
      deps: [EnvironmentService, AuthConfigService],
      multi: true,
    },
    {
      provide: OKTA_CONFIG,
      deps: [OktaAuthConfig],
      useFactory: (oktaConfig: OktaAuthConfig) => ({
        oktaAuth: new OktaAuth({
          ...oktaConfig.config,
        }),
        onAuthRequired: (oktaAuth: OktaAuth, injector: Injector) => {
          const triggerLogin = async () => {
            await oktaAuth.signInWithRedirect();
          };
          if (
            !oktaAuth.authStateManager.getPreviousAuthState()?.isAuthenticated
          ) {
            triggerLogin();
          } else {
            const modalService = injector.get(SuiModalService);
            modalService
              .open(
                new ConfirmModal(
                  'Do you want to re-authenticate?',
                  'Auth required',
                  'Yes',
                  'No'
                )
              )
              .onApprove(triggerLogin)
              .onDeny(() => {});
          }
        },
      }),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddHeaderInterceptor,
      multi: true,
    },
    EnvironmentService,
    DeviceDetectorService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
